
import React, { useState, useRef, useEffect } from 'react';
import { BusinessIdea } from '@/src/types';
import { useReporting } from '@/src/hooks/useReporting';
import { useAuth } from '@/src/contexts/AuthContext';
import { verifyEntitySpotting } from '@/src/services/verificationService';

interface Props {
  idea: BusinessIdea;
  onClose: () => void;
  isSaved: boolean;
  onToggleSave: () => void;
  onUpgradePrompt: () => void;
}

type TabType = 'recon' | 'sites';

const DiscoveryDossier: React.FC<Props> = ({ idea, onClose, isSaved, onToggleSave, onUpgradePrompt }) => {
  const { isLoggedIn, onSignUpPrompt, userProfile } = useAuth();
  const { exportBusinessSnapshot } = useReporting();
  const [activeTab, setActiveTab] = useState<TabType>('recon');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Entity spotting verification states
  const [spottingResults, setSpottingResults] = useState<Record<string, { confirmed: boolean; missingFromMapsButOnline: boolean; discrepancies: string[]; lastVerified: string }>>({});
  const [checkingSpottingId, setCheckingSpottingId] = useState<string | null>(null);

  const subscriptionTier = userProfile?.subscriptionTier || 'free';
  const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [activeTab]);

  return (
    <div className="fixed inset-0 z-[150] bg-[#322D29] flex flex-col animate-slide-up overflow-hidden text-white selection:bg-[#F26522]/30 font-sans">
      <header className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between bg-[#322D29] sticky top-0 z-50">
        <button onClick={onClose} className="flex items-center gap-2 text-white/60 hover:text-white transition-all group px-3 py-2 cursor-pointer">
          <i className="fas fa-chevron-left text-xs group-hover:-translate-x-1"></i>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] font-heading">Exit Brief</span>
        </button>
        <div className="flex items-center gap-3">
          <button 
            onClick={onToggleSave}
            className={`px-4 py-2 rounded-lg border text-[9px] font-bold uppercase tracking-widest transition-all font-heading cursor-pointer ${
              isSaved ? 'bg-[#3a3430] border-red-500/40 text-red-400' : 'bg-[#3a3430] border-white/[0.06] text-white/60 hover:text-white'
            }`}
          >
            <i className={`fa-${isSaved ? 'solid' : 'regular'} fa-heart mr-2`}></i>
            {isSaved ? 'Intel Saved' : 'Save Intel'}
          </button>
          <button 
            onClick={() => {
              if (!isLoggedIn) {
                onSignUpPrompt();
                return;
              }
              if (!isPremium) {
                onUpgradePrompt();
                return;
              }
              exportBusinessSnapshot(idea);
            }} 
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:text-white border border-white/[0.06] bg-[#3a3430] hover:bg-white/5 transition-all cursor-pointer"
            title="Download Snapshot"
          >
            <i className="fas fa-download text-xs"></i>
          </button>
        </div>
      </header>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto no-scrollbar bg-[#322D29]">
        <section className="px-4 sm:px-8 pt-8 md:pt-14 pb-10 max-w-4xl mx-auto bg-[#322D29] border-b border-white/[0.06]">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#3a3430] border border-white/[0.06] text-white">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F26522] animate-pulse"></span>
              <span className="text-[9px] font-bold uppercase tracking-[0.25em] font-heading text-white/80">Factual Intelligence Dossier</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight text-white font-heading uppercase">{idea.title}</h1>
            <p className="text-base sm:text-lg font-medium text-white/70 leading-relaxed border-l-2 border-[#F26522] pl-4 sm:pl-6 font-dm">{idea.description}</p>
          </div>
        </section>

        <nav className="sticky top-0 z-40 bg-[#322D29] border-b border-white/[0.06] overflow-x-auto no-scrollbar">
          <div className="max-w-4xl mx-auto px-4 sm:px-8 flex justify-between min-w-max gap-8 sm:gap-0">
            {[
              { id: 'recon', label: 'Field Recon' },
              { id: 'sites', label: 'Site Recon' }
            ].map((tab) => {
              if (!tab || !tab.id) return null;
              return (
              <button 
                key={`tab-${tab.id}`} 
                onClick={() => setActiveTab(tab.id as any)} 
                className={`py-4 relative text-[10px] font-bold uppercase tracking-[0.25em] font-heading transition-all shrink-0 cursor-pointer ${activeTab === tab.id ? 'text-white' : 'text-white/60 hover:text-white'}`}
              >
                {tab.label}
                {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F26522]"></div>}
              </button>
            )})}
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-4 sm:px-8 py-8 md:py-10 pb-36">
          {activeTab === 'recon' && (
            <div className="space-y-10 animate-fade-in">
              <div className="grid grid-cols-1 gap-6">
                <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-heading">Observed Density</span>
                  <p className="text-2xl sm:text-3xl font-bold text-white font-heading">{idea.densityInsight || 'Significant Presence'}</p>
                  <div className="h-0.5 w-16 bg-[#F26522]"></div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#3a3430] border border-white/[0.06] text-[#F26522] flex items-center justify-center">
                    <i className="fas fa-satellite-dish text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-white font-heading leading-tight">Operational Directory</h3>
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 font-heading">Verified Geographic Grounding</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5">
                  {idea.spottedEntities?.map((entity, i) => {
                    if (!entity || !entity.name) return null;
                    return (
                    <div key={`dossier-entity-${entity.name}-${i}`} className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl hover:border-white/20 transition-all relative overflow-hidden">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-2 relative z-10">
                        <div className="space-y-1">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Active Entity</span>
                          <h4 className="text-xl sm:text-2xl font-bold text-white font-heading leading-tight">{entity.name}</h4>
                        </div>
                        <div className="flex gap-3">
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entity.address || entity.name)}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-lg bg-[#322D29] border border-white/[0.06] text-white flex items-center justify-center hover:bg-[#F26522] transition-colors cursor-pointer"
                            title="Open in Maps"
                          >
                            <i className="fas fa-directions text-xs"></i>
                          </a>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-5 border-t border-white/[0.06] relative z-10">
                        <div className="space-y-1">
                          <span className="text-[8px] font-bold uppercase text-white/40 font-heading">Exact Location</span>
                          <p className="text-xs font-medium text-white/80 uppercase tracking-wide font-dm">{entity.address || 'Verified Corridor'}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[8px] font-bold uppercase text-white/40 font-heading">Protocol</span>
                          <p className="text-xs font-medium text-white/80 truncate font-dm">{entity.contact || 'Direct Presence'}</p>
                        </div>
                      </div>
                      {entity.description && (
                        <p className="mt-4 text-xs font-medium text-white/70 italic leading-relaxed relative z-10 font-dm">"{entity.description}"</p>
                      )}

                      {/* Web-integrity Verification */}
                      <div className="mt-5 pt-4 border-t border-white/[0.06] relative z-10 text-left">
                        {spottingResults[entity.name] ? (
                          <div className="space-y-2 bg-[#322D29] p-4 border border-white/[0.06] rounded-lg">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-[9px] font-bold uppercase text-white/40 font-heading">Web Verification Status</span>
                              <span className="text-[8px] font-medium text-white/40 text-right">Last Verified: {spottingResults[entity.name].lastVerified}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wider rounded ${spottingResults[entity.name].confirmed ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-orange-500/20 text-orange-300 border border-orange-500/30'}`}>
                                {spottingResults[entity.name].confirmed ? '● Entity Confirmed on Web Index' : '● Unconfirmed on Web Index'}
                              </span>
                              {spottingResults[entity.name].missingFromMapsButOnline && (
                                <span className="px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wider bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded">
                                  ● Active Online / Missing from Maps
                                </span>
                              )}
                            </div>
                            <div className="space-y-1 mt-2">
                              {spottingResults[entity.name].discrepancies.map((discrepancy, idx) => (
                                <p key={`discrepancy-${idx}`} className="text-[11px] font-medium text-white/70 leading-normal flex items-start gap-1.5 font-dm">
                                  <i className="fas fa-triangle-exclamation text-[#F26522] mt-0.5 text-[9px]"></i>
                                  {discrepancy}
                                </p>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between bg-[#322D29] p-3 border border-white/[0.06] rounded-lg">
                            <span className="text-[10px] font-bold uppercase text-white/40 font-heading">Entity Spotting Integrity Check</span>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                setCheckingSpottingId(entity.name);
                                try {
                                  const res = await verifyEntitySpotting(idea.locationName || 'Kampala', entity.name, userProfile?.id || 'anonymous');
                                  setSpottingResults(prev => ({ ...prev, [entity.name]: res }));
                                } catch (err) {
                                  console.error(err);
                                } finally {
                                  setCheckingSpottingId(null);
                                }
                              }}
                              disabled={checkingSpottingId !== null}
                              className="px-3 py-1.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase tracking-[0.1em] text-[9px] rounded cursor-pointer transition-all flex items-center gap-2 font-heading"
                            >
                              {checkingSpottingId === entity.name ? (
                                <>
                                  <i className="fas fa-circle-notch animate-spin text-[8px]"></i>
                                  Verifying Presence...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-globe text-[8px]"></i>
                                  Verify Web Existence
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )})}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sites' && (
            <div className="space-y-8 animate-fade-in">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40 font-heading">Tactical Site Selection</h3>
              <div className="grid grid-cols-1 gap-5">
                {idea.siteRecommendations?.map((site, i) => {
                  if (!site || !site.name) return null;
                  return (
                  <div key={`dossier-site-${site.name}-${i}`} className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl hover:border-white/20 transition-all">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className="w-10 h-10 shrink-0 rounded-lg bg-[#322D29] border border-white/[0.06] text-[#F26522] flex items-center justify-center font-bold text-sm font-heading">0{i + 1}</div>
                      <div className="space-y-1.5">
                        <h4 className="text-lg font-bold text-white font-heading">{site.name}</h4>
                        <p className="text-xs font-medium leading-relaxed text-white/70 italic font-dm">"{site.reason}"</p>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            </div>
          )}
        </main>
      </div>

      <footer className="p-5 border-t border-white/[0.06] bg-[#322D29] grid grid-cols-2 gap-4">
        <button 
          onClick={onToggleSave}
          className={`py-3.5 rounded-lg font-bold uppercase text-[10px] tracking-[0.25em] font-heading transition-all border cursor-pointer ${
            isSaved ? 'bg-[#3a3430] text-red-400 border-red-500/30' : 'bg-[#3a3430] text-white/70 hover:text-white border-white/[0.06] hover:bg-white/5'
          }`}
        >
          {isSaved ? 'Purge Intel' : 'Save Intel'}
        </button>
        <button 
          onClick={onClose} 
          className="bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3.5 rounded-lg font-bold uppercase text-[10px] tracking-[0.25em] font-heading transition-all cursor-pointer"
        >
          Close Intelligence Brief
        </button>
      </footer>
    </div>
  );
};

export default DiscoveryDossier;
